import config from '../../config/index';
import {Log, User, UserManager, WebStorageStateStore} from "oidc-client-ts";
import axios from 'axios';
import * as _ from 'lodash';
import {getAdminPermissions} from "../../api/organization-unit-admin-functions";
import {OrgUnitPermission} from "../../models/admin-permission.models";
import {UserInfo} from "../../models/user-info.model";

export interface  IAuthService {
    signinRedirectCallback: () => Promise<User | undefined>,
    getSsoUser: () => Promise<User>,
    getUser: () => Promise<UserInfo | null>,
    getPermissions: (organizationId: number, args: any) => Promise<OrgUnitPermission | {[organizationUnitId: number]: OrgUnitPermission}>,
    isAuthenticated: () => boolean,
    getAccessToken: () => string | null,
    signoutRedirectCallback: () => void,
    logout: () => void,
    getRoles: () => Promise<string[]>
}

export class AuthService implements IAuthService{
    userManager: UserManager;
    constructor() {
        Log.setLogger(console);
        Log.setLevel(Log.INFO);

        this.userManager = new UserManager({
            ...config.ssoConfig,
            userStore: new WebStorageStateStore({ store: window.sessionStorage }),
            stateStore: new WebStorageStateStore({ store: window.sessionStorage })
        });
        this.userManager.events.addAccessTokenExpiring(() => console.info('Access token is expiring...'));
        this.userManager.events.addUserUnloaded(() => console.info('user unloaded triggered...'));
        this.userManager.events.addUserSessionChanged(() => console.warn('user session changed'))
        this.userManager.events.addSilentRenewError((e) => console.log("silent renew error", e.message));
        this.userManager.events.addUserSignedOut(() => {
            console.info('user logged out at OP, signing out...')
            this.logout();
        });
        this.userManager.events.addAccessTokenExpired(() => {
            console.info('Access token has expired, logging out');
            this.logout();
        });
        this.userManager.events.addUserLoaded((user) => {
            if (window.location.href.indexOf("callback") !== -1) {
                this.navigateBackToSecuredPage();
            }
        });

        document.addEventListener("click", async (e) => {
            await this.userManager.signinSilent();
        });
    }

    signinRedirectCallback: () => Promise<User | undefined> = () => {
        return this.userManager.signinCallback();
    };

    getSsoUser = async () => {
        const user = await this.userManager.getUser();
        if (!user) {
            return await this.userManager.signinRedirectCallback();
        }
        return user;
    };

    getUser = (() => {
        let currentUser: UserInfo | null = null;
        return async () => {
            if (!currentUser) {
                currentUser = await this.fetchUser();
            }
            return currentUser ?? null;
        };
    })();

    getPermissions: (organizationId: number, args: any) => Promise<{ [organizationUnitId: number]: OrgUnitPermission; } | OrgUnitPermission> = (() => {
        const orgUnitPermissions: {[organizationUnitId: number]: OrgUnitPermission} = {};
        return async (organizationId: number, { organizationUnitId, organizationUnitParentId}: any) => {
            // When fetching the permissions of a parent organization unit we cannot rely on the cache
            if (!organizationUnitParentId && orgUnitPermissions[organizationUnitId])
                return orgUnitPermissions[organizationUnitId];
            else {
                const user = await this.getUser();
                if(!user) return orgUnitPermissions;

                const { permissions } = await getAdminPermissions(organizationId, user.Id, { organizationUnitId, organizationUnitParentId });
                if (permissions) {
                    permissions.forEach(p => orgUnitPermissions[p.organizationUnitId] = p);
                }
                return orgUnitPermissions;
            }
        };
    })();

    getRoles = async (): Promise<string[]> => {
        const u = await this.getSsoUser();
        const jwt = this.parseJwt(u.access_token);
        if (!jwt)
            return [];
        const roleKey = _.keys(jwt).find((k: any) => k.indexOf('claims/role') > -1);
        return roleKey ? (Array.isArray(jwt[roleKey]) ? jwt[roleKey] : [jwt[roleKey]]) : [];
    };

    signinRedirect: () => void = () => {
        sessionStorage.setItem("redirectUri", window.location.pathname);
        this.userManager.signinRedirect({});
    };

    navigateBackToSecuredPage: () => void = () => {
        const intendedTarget = sessionStorage.getItem("redirectUri");
        const isRelative = new RegExp("^/[^/]");
        const target = intendedTarget && isRelative.test(intendedTarget) ? intendedTarget : "/";
        window.location.replace(target);
    };

    getAccessToken: () => string | null = () => {
        const oidcKey = `oidc.user:${config.ssoConfig.authority}:${config.ssoConfig.client_id}`;
        const oidcStorageJson =  sessionStorage.getItem(oidcKey);
        const oidcStorage = oidcStorageJson ? JSON.parse(oidcStorageJson) : null;
        return oidcStorage ? oidcStorage.access_token : null;
    };

    isAuthenticated: () => boolean = () => !!this.getAccessToken();

    logout: () => void = () => {
        this.userManager.signoutRedirect({
            id_token_hint: sessionStorage.getItem("id_token") ?? ""
        }).then(() => {
            return this.userManager.clearStaleState();
        });
    };


    signoutRedirectCallback: () => void = () => {
        this.userManager.signoutRedirectCallback().then(() => {
            sessionStorage.clear();
            window.location.replace(process.env.REACT_APP_PUBLIC_URL as string);
        });
        this.userManager.clearStaleState();
    };

    private parseJwt = (token: string) => {
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace("-", "+").replace("_", "/");
        return JSON.parse(window.atob(base64));
    };

    private fetchUser = async (): Promise<UserInfo | null> => {
        const u = await this.getSsoUser();
        if (!u.access_token || !u.profile.sub)
            return null;
        const resp = await axios.post(`${config.api}/Account/GetMyUserInfo`, { Body: { UserIds: [u.profile.sub] } });
        if (!resp.data || !resp.data.Body)
            return null;
        return resp.data.Body;
    };
}
const authService = new AuthService();
export default authService;
